<template>
  <div class="primary-navigation__cart siteMenu__button">
    <button
      ref="miniCartButton"
      v-tippy="tippy"
      aria-haspopup="true"
      qaid="b2c-mini-cart-menu-button"
      :title="$t('minicart.cart.alt')"
      data-ga-cart="cart"
      :data-ga-label="totalItems"
      class="header-bottom__cart-button menu-trigger flex"
      @focusout="handleFocus"
      @keyup.esc="closeTooltip"
    >
      <pdl-icon qaid="mini-cart-icon" name="shopping_cart" />
      <span class="sr-only" v-text="miniCartSrText" />
      <pdl-badge
        v-if="totalItems > 0"
        id="miniCartItemCount"
        qaid="mini-cart-badge"
        class="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"
        :value="totalItems"
        aria-hidden="true"
      />
    </button>

    <div v-cloak ref="minicart" :class="['text-sm p-2 overflow-y-auto b2c-mini-cart', {'py-4': items.length < 1}]">
      <pdl-loading
        v-if="isLoading && items.length < 1"
        :is-loading="isLoading"
        qaid="b2c-mini-cart-loading"
        :message="$t('text.loadingMessage')"
      />

      <template v-if="totalItems < 1">
        <pdl-heading
          v-if="!isLoading"
          qaid="b2c-mini-cart-empty"
          :level="4"
          size="sm"
          v-text="$t('popup.cart.empty')"
        />
      </template>
      <template v-else>
        <div qaid="b2c-mini-cart-items">
          <mini-cart-item v-for="item in items" :key="item.sku" :item="item" />
        </div>

        <div class="mt-3 mb-3 flex justify-between">
          <div v-text="$t('text.order.subtotal')" />
          <div v-text="subTotalPrice" />
        </div>

        <div v-if="appliedOrderPromotions.length || appliedProductPromotions.length" class="flex justify-between">
          <div v-text="$t('basket.couponCode.discount')" />
          <div v-text="totalDiscounts" />
        </div>

        <div class="mb-3 flex justify-between text-lg font-bold">
          <div v-text="$t('popup.cart.total')" />
          <div qaid="b2c-mini-cart-total-price" v-text="totalPrice" />
        </div>

        <div v-if="hasVatTax" class="justify-between mb-2 flex italic">
          <div v-text="$t('basket.page.totals.netTotal')" />
          <div v-text="cartNetTotalValue" />
        </div>

        <div v-if="hasVatTax" class="justify-between mb-2 flex italic">
          <div v-text="$t('basket.page.totals.estimatedVAT')" />
          <div v-text="cartTaxesValue" />
        </div>

        <pdl-link qaid="b2c-mini-cart-view-cart-link" block button primary :href="cartUrl">
          {{ $t('basket.view.basket') }}
        </pdl-link>
      </template>
    </div>
  </div>
</template>

<script>
import {mapState, mapActions} from 'vuex';
import {PdlBadge} from '@pedal/pdl-badge';
import {PdlIcon} from '@pedal/pdl-icon';
import {PdlLink} from '@pedal/pdl-link';
import {PdlLoading} from '@pedal/pdl-loading';
import {PdlHeading} from '@pedal/pdl-section-header';
import MiniCartItem from '@/components/mini-cart/b2c/MiniCartItem';
import NumberFormatter from '@/utils/NumberFormatter';
import {directive} from 'vue-tippy';
import {useCartUtils} from '@/composables/cart-utils.js';
import {focusTrap, getAllFocusableElements} from '@/utils/accessibility/a11y-utils';

const numberFormatter = new NumberFormatter({priceFormatter: window.ACC.customVariables.priceFormatter});

export const UPDATE_MINI_CART = 'update.mini.cart';
export default {
  components: {PdlBadge, PdlLink, PdlIcon, PdlLoading, PdlHeading, MiniCartItem},
  directives: {tippy: directive},
  setup() {
    const {cartUrl} = useCartUtils();

    return {cartUrl};
  },
  data() {
    return {
      focusableElements: [],
      isInContainerConfigMode: false,
      tippy: {
        allowHTML: true,
        content: this.getMiniCart,
        interactive: true,
        trigger: 'click',
        placement: 'bottom-end',
        theme: 'flush',
        sticky: true,
        appendTo: document.body,
        popperOptions: {
          strategy: 'fixed',
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                tether: false,
              },
            },
            {
              name: 'flip',
              enabled: true,
            },
          ],
        },
        onShow: this.fetchMiniCart,
        onShown: this.focusMiniCartTooltip,
        onHidden: this.revertFocusBackToBtn,
      },
    };
  },
  computed: {
    ...mapState('backend', ['trekBrandNavURL', 'isElectraBrand']),
    ...mapState('miniCart', ['cart', 'isLoading']),
    ...mapState('user', ['selectedRetailer', 'selectedLocation']),
    items() {
      return this.cart?.entries ?? [];
    },
    totalItems() {
      return this.items.reduce((total, entry) => total + entry.quantity, 0);
    },
    totalPrice() {
      if (this.appliedOrderPromotions.length || this.appliedProductPromotions.length) {
        if (this.items.length) {
          return numberFormatter.formatPrice(this.cart?.totalPrice.value);
        }
        return numberFormatter.formatPrice(this.cart?.totalPriceWithoutCharges?.value);
      }

      return numberFormatter.formatPrice(this.cart?.itemGrossPrice.value);
    },
    subTotalPrice() {
      return numberFormatter.formatPrice(this.cart?.itemGrossPrice?.value);
    },
    totalDiscounts() {
      return numberFormatter.formatPrice(this.cart?.totalDiscounts?.value);
    },
    appliedOrderPromotions() {
      return this?.cart?.appliedOrderPromotions ?? [];
    },
    appliedProductPromotions() {
      return this?.cart?.appliedProductPromotions ?? [];
    },
    hasVatTax() {
      return this?.cart?.displayTaxB2CEnum === 'VAT';
    },
    cartTaxesValue() {
      const cartTaxes = this?.cart?.cartTaxes;
      return cartTaxes?.value > 0 ? numberFormatter.formatPrice(cartTaxes.value) : this.$t('text.free');
    },
    cartNetTotalValue() {
      const cartNetTotal = this?.cart?.cartNetTotal;
      return cartNetTotal?.value > 0 ? numberFormatter.formatPrice(cartNetTotal.value) : this.$t('text.free');
    },
    miniCartSrText() {
      return this.$t('basket.added.to.basket.quantity', [this.totalItems]);
    },
  },
  mounted() {
    this.fetchMiniCart();
  },
  methods: {
    ...mapActions('miniCart', ['fetchMiniCart']),

    revertFocusBackToBtn() {
      if (!this.totalItems) return;
      this.$refs['miniCartButton']?.focus();
      document.removeEventListener('keydown', this.focusTrapMiniCartTooltip);
      document.removeEventListener('keyup', this.handleKeyup);
    },

    focusMiniCartTooltip() {
      if (!this.totalItems) return;
      this.focusableElements = getAllFocusableElements(this.$refs['miniCartButton']._tippy?.popper);
      document.addEventListener('keydown', this.focusTrapMiniCartTooltip);
      document.addEventListener('keyup', this.handleKeyup);
      this.focusableElements[0].focus();
    },

    focusTrapMiniCartTooltip(event) {
      focusTrap(event, this.focusableElements);
    },

    handleFocus(event) {
      if (!this.totalItems && event.target === this.$refs['miniCartButton']) {
        this.closeTooltip();
      }
    },

    handleKeyup(event) {
      if (event.key === 'Escape') {
        this.closeTooltip();
      }
    },

    closeTooltip() {
      const miniCartButton = this.$refs['miniCartButton'];
      if (miniCartButton?._tippy) {
        miniCartButton._tippy.hide();
        this.focusableElements = [];
      }
    },
    getMiniCart() {
      return this.$refs.minicart;
    },
  },
};
</script>
<style lang="scss" scoped>
.b2c-mini-cart {
  max-height: 75vh;
  width: 28rem;
  max-width: 100%;
}
</style>
